/**
 * Amorçage du client SnowChain :
 * session (invité ou compte) -> hall (WebSocket du menu) -> menu principal (serveurs,
 * parties, amis) -> partie choisie : connexion temps réel (Web Worker), accord du serveur,
 * rendu (WebGPU, sinon WebGL2), assets, premier snapshot -> moteur + HUD.
 * `?world=<code>` saute le menu et entre directement dans la partie (lien d'invitation).
 */
import { dayCosts, dayRevenue, type LobbyNotice } from '@snowchain/shared';
import './hud/tokens.css';
import './hud/hud.css';
import './hud/game.css';
import './menu/menu.css';
import { render } from 'preact';
import { checkSession, clearSession, logout, savedSession, type Account } from './auth';
import { ClientStore } from './state/store';
import { NetClient } from './net/net-client';
import { createRenderer, Engine } from './render/engine';
import { loadAssets } from './render/assets';
import { GameHud } from './hud/App';
import { ErrorScreen, LoadingScreen, LoginScreen } from './hud/screens';
import { hud } from './hud/context';
import { toast, ui } from './hud/ui-state';
import { euros } from './hud/format';
import { sampleCash } from './hud/trends';
import { lobby } from './menu/lobby';
import { MainMenu } from './menu/MainMenu';
import { InGameSocial, menuUrl } from './menu/ingame';
import { menu, menuActions } from './menu/state';

/** Dernier fait du journal déjà signalé (les bouchons et ruptures arrivent par l'historique, pas par les événements). */
let lastJournal = -1;
function flushJournal(store: ClientStore): void {
  const journal = store.replica?.usage?.journal;
  if (!journal?.length) return;
  const newest = journal[journal.length - 1]!.id;
  if (lastJournal < 0) {
    lastJournal = newest;
    return;
  }
  for (const e of journal) {
    if (e.id <= lastJournal) continue;
    if (e.title.startsWith('Bouchon') || e.title.startsWith('Plus de skis')) toast(e.level, e.title, e.text, 7000);
  }
  lastJournal = newest;
}

const root = document.getElementById('hud') as HTMLElement;
const canvas = document.getElementById('scene') as HTMLCanvasElement;

declare global {
  interface Window {
    __snowchain?: Record<string, unknown>;
  }
}

function show(node: preact.ComponentChild): void {
  render(node, root);
}

async function boot(): Promise<void> {
  const saved = savedSession();
  if (saved) {
    const account = await checkSession(saved.token).catch(() => null);
    if (account) return enter(saved.token, account);
    clearSession();
  }
  show(<LoginScreen onReady={(s) => enter(s.token, s.account)} />);
}

/** Session ouverte : le hall se connecte, puis le menu (ou la partie d'un lien d'invitation). */
function enter(token: string, account: Account): void {
  menuActions.token = () => token;
  menuActions.join = (worldId) => void start(token, account, worldId);
  menuActions.logout = () => void signOut(token);
  lobby.onNotice = onNotice;
  lobby.onAuthError = () => {
    clearSession();
    location.assign(menuUrl());
  };
  lobby.connect(token);
  const world = new URLSearchParams(location.search).get('world');
  if (world) void start(token, account, world);
  else showMenu();
}

function showMenu(error?: { title: string; message: string }): void {
  setWorldParam(null);
  hud.store = null;
  menu.joining.value = null;
  if (error) menu.dialog.value = { k: 'message', ...error };
  show(<MainMenu />);
}

async function signOut(token: string): Promise<void> {
  lobby.close();
  await logout(token);
  location.assign(menuUrl());
}

/** La partie en cours figure dans l'URL : un rechargement y revient et le lien se partage. */
function setWorldParam(worldId: string | null): void {
  const url = new URL(location.href);
  if (worldId) url.searchParams.set('world', worldId);
  else url.searchParams.delete('world');
  if (url.href !== location.href) history.replaceState(null, '', url);
}

function onNotice(n: LobbyNotice): void {
  switch (n.k) {
    case 'friend-request':
      toast('info', 'Demande d’ami', `${n.from.name} veut vous ajouter à ses amis.`, 7000);
      break;
    case 'friend-accepted':
      toast('ok', 'Nouvel ami', `${n.by.name} a accepté votre demande.`);
      break;
    case 'invite': {
      // Même nom que le bandeau et la liste : la station d'abord, sinon le nom du monde.
      const where = n.invite.world.stationName || n.invite.world.name;
      toast(
        'info',
        `Invitation de ${n.invite.from.name}`,
        hud.engine ? `« ${where} » : ouvrez le panneau Amis pour la rejoindre.` : `« ${where} » : rejoignez-la depuis l’accueil ou le panneau Amis.`,
        9000,
      );
      break;
    }
    case 'friend-online':
      // En jeu, seule la pastille du bouton Amis change : pas de toast à chaque connexion.
      if (!hud.engine) toast('info', `${n.friend.name} est en ligne`, undefined, 3500);
      break;
  }
}

async function start(token: string, account: Account, worldId: string): Promise<void> {
  setWorldParam(worldId);
  show(<LoadingScreen label="Connexion à la partie..." progress={0.04} />);
  const store = new ClientStore();
  hud.store = store;
  const net = new NetClient((e) => store.handle(e));
  store.sendCommand = (cmd) => net.send(cmd);
  net.connect(token, worldId);

  // Le serveur accepte (premier snapshot) ou refuse (partie complète ou supprimée, session expirée)
  // avant tout chargement : un refus ramène au menu sans avoir créé de contexte 3D.
  const entry = await new Promise<'ok' | 'auth' | 'world'>((resolve) => {
    const unsub = store.subscribe(() => {
      const r = store.replica ? 'ok' : store.status === 'auth-error' ? 'auth' : store.status === 'world-error' ? 'world' : null;
      if (!r) return;
      unsub();
      resolve(r);
    });
  });
  if (entry === 'auth') {
    net.dispose();
    lobby.close();
    clearSession();
    show(<LoginScreen onReady={(s) => enter(s.token, s.account)} />);
    return;
  }
  if (entry === 'world') {
    net.dispose();
    showMenu({ title: 'Impossible de rejoindre la partie', message: store.statusDetail || 'Partie indisponible.' });
    return;
  }
  show(<LoadingScreen label="Préparation du rendu 3D..." progress={0.1} />);
  let ctx;
  try {
    ctx = await createRenderer(canvas);
  } catch (e) {
    console.error(e);
    net.dispose();
    show(<ErrorScreen title="3D indisponible" message="Ni WebGPU ni WebGL2 ne sont disponibles dans ce navigateur." onRetry={() => location.assign(menuUrl())} />);
    return;
  }
  const lib = await loadAssets(ctx.renderer, (p) => show(<LoadingScreen label="Chargement des modèles 3D..." progress={0.15 + p * 0.65} />));
  show(<LoadingScreen label="Construction du domaine..." progress={0.85} />);
  // Les notifications du menu n'ont plus d'objet en jeu.
  ui.toasts.value = [];
  await new Promise((r) => setTimeout(r, 30));
  const engine = new Engine(ctx, canvas, store, lib);
  hud.engine = engine;
  engine.start();

  // Pont store -> HUD : au plus ~6 rafraîchissements par seconde.
  let scheduled = false;
  store.subscribe(() => {
    if (scheduled) return;
    scheduled = true;
    setTimeout(() => {
      scheduled = false;
      ui.tick.value++;
      flushEvents(store);
      flushJournal(store);
      const me = store.replica?.me;
      if (me) sampleCash(store.clock.gameNow(), me.cash);
    }, 160);
  });
  setInterval(() => ui.tick.value++, 1000);
  show(
    <>
      <GameHud />
      <InGameSocial />
    </>,
  );
  if (lib.fallback) toast('warn', 'Modèles 3D de secours', 'Le pack d\u2019assets est introuvable : lancez « npm run assets ».', 8000);
  toast('info', `Bienvenue, ${account.name}`, store.isOwner ? `Vous gérez « ${store.replica?.station.name} ».` : 'Vous observez cette station.');
  window.__snowchain = { store, engine, perf: () => ({ ...ui.perfStats.value, history: engine.perf.history }) };
}

function flushEvents(store: ClientStore): void {
  while (store.events.length) {
    const ev = store.events.shift()!;
    switch (ev.k) {
      case 'built':
        toast('ok', 'Mise en service', ev.label);
        break;
      case 'breakdown':
        toast('danger', 'Panne', `${ev.label} est à l'arrêt.`);
        break;
      case 'repaired':
        toast('ok', 'Réparée', `${ev.label} redémarre.`);
        break;
      case 'delivery':
        toast('info', 'Livraison reçue', `${ev.qty} ${ev.supply === 'food' ? 'repas' : 'L de carburant'}`);
        break;
      case 'dayEnd': {
        const r = ev.report;
        const result = dayRevenue(r) - dayCosts(r);
        toast(result >= 0 ? 'ok' : 'warn', `Bilan du jour ${r.day}`, `${r.visitors} visiteurs · satisfaction ${r.satisfaction} % · résultat ${euros(result)}`, 9000);
        break;
      }
      case 'notice':
        toast(ev.level, ev.title, ev.message);
        break;
    }
  }
}

void boot();
